'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface IProps<T> {
  children: React.ReactNode,
  defaultTab?: T,
  onChange?: (tabId: T) => void,
}
const useTabs = <T = string | number | symbol>({
  defaultTab,
  onChange,
}: Omit<IProps<T>, 'children'>) => {
  const [selectedTabId, setSelectedTabId] = useState<T>(defaultTab);

  useEffect(() => {
    setSelectedTabId(defaultTab);
  }, [defaultTab]);

  const select = (tabId: T) => {
    setSelectedTabId(tabId);
    onChange && onChange(tabId);
  };

  const isSelected = (tabId: T) => tabId === selectedTabId;

  return {
    selectedTabId,
    select,
    isSelected,
  };
};

type TTabsContext = ReturnType<typeof useTabs>;

export const TabsContext = createContext<TTabsContext>(null);

export const useTabsContext = () => useContext(TabsContext);

const TabsContextProvider = <T = string | number | symbol>({ children, defaultTab, onChange } : IProps<T>) => (
  <TabsContext.Provider value={ useTabs<T>({ defaultTab, onChange }) }>
    { children }
  </TabsContext.Provider>
);

export default TabsContextProvider;
